import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import classNames from 'classnames'
import { useLastPathSegment } from 'shared/hooks'
import { makeTextFieldIcon } from 'shared/ui/TextField'
import { Box, InputCheckbox, TextFieldWithLabel, Typography } from 'shared/ui'
import { getAriaLabel } from 'shared/lib'
import { PageDescription } from 'pages/settings/ui/PageDescription/PageDescription'
import { useInternationalStepsContext } from 'pages/settings/pages/compliance/pages/international/context/internationalStepsContext'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import { type InternationalRoutes } from 'pages/settings/pages/compliance/pages/international'
import { ErrorBanner } from 'pages/settings/pages/compliance/pages/international/ui/ErrorBanner/ErrorBanner'
import { RegistrationAuthoritySelect } from 'pages/settings/pages/compliance/ui/RegistrationAuthoritySelect'
import styles from './styles.module.scss'

export const BusinessInfo = observer(() => {
  const {
    stepsStore: { stepLoading },
    moveToSecondStep,
    businessInfoStore,
    errorMessage,
    initializeStepsForRoute,
    nextDisabled,
  } = useInternationalStepsContext()
  const lastPathSegment = useLastPathSegment()

  const {
    businessName,
    registrationAuthorityStore,
    websiteUrl,
    noWebsiteUrl,
    websiteUrlLocalError,
    isCorrectUrl,
    socialMediaUrl,
    socialMediaUrlError,
    einFormatted,
    einPlaceholder,
    einErrorMessage,
    addBusinessName,
    setEinSearch,
    checkEin,
    setWebsiteUrl,
    setSocialMediaUrl,
    checkUrl,
    handleSetNoWebsiteUrl,
    resetEinSearch,
  } = businessInfoStore

  useEffect(() => {
    initializeStepsForRoute(lastPathSegment as InternationalRoutes)
  }, [initializeStepsForRoute, lastPathSegment])

  return (
    <div className={styles.wrap}>
      <Box marginBottom={errorMessage ? 0 : 5}>
        <PageDescription>
          Make sure this information is an exact match to your official paperwork
        </PageDescription>
      </Box>
      <ErrorBanner />
      <TextFieldWithLabel
        marginBottom={3}
        labelProps={{
          label: {
            text: 'Legal business name',
          },
        }}
        textFieldProps={{
          className: styles.textInput,
          value: businessName || '',
          onChange: addBusinessName,
          InputProps: {
            placeholder: 'e.g. Example LLC',
          },
        }}
        bottomLabelProps={{
          label: {
            text: (
              <Typography
                variant='text-input-helper'
                ariaLabel={getAriaLabel('Legal business name')}
              >
                Enter the exact legal business name as used for business registration
              </Typography>
            ),
          },
        }}
      />
      <RegistrationAuthoritySelect
        store={registrationAuthorityStore}
        onCountryChange={resetEinSearch}
      />
      <TextFieldWithLabel
        marginBottom={3}
        errorMessage={einErrorMessage}
        labelProps={{
          label: {
            text: 'Business registration number',
          },
        }}
        textFieldProps={{
          className: styles.textInput,
          value: einFormatted,
          onChange: setEinSearch,
          onBlur: checkEin,
          InputProps: {
            placeholder: einPlaceholder,
          },
        }}
        bottomLabelProps={
          !einErrorMessage
            ? {
                label: {
                  text: (
                    <Typography
                      variant='text-input-helper'
                      ariaLabel={getAriaLabel('Business registration number')}
                    >
                      Business registration number provided by the registration authority
                    </Typography>
                  ),
                },
              }
            : {}
        }
      />
      <TextFieldWithLabel
        marginBottom={0}
        errorMessage={websiteUrlLocalError}
        labelProps={{
          marginBottom: 4,
          questionTooltipProps: {
            label: 'You need a website to register',
            desc: 'If you don’t have an official website and are using a social media profile page, click “I don’t have a website”.',
            type: 'description',
            width: 240,
          },
          label: {
            text: 'Business website URL',
          },
        }}
        textFieldProps={{
          disabled: noWebsiteUrl,
          value: websiteUrl || '',
          error: !!websiteUrlLocalError,
          onChange: setWebsiteUrl,
          onBlur: (value: string) => checkUrl(value, 'websiteUrl'),
          InputProps: {
            placeholder: 'e.g. https://www.example.com/',
          },
          className: styles.selectPadding,
        }}
        rightActions={[
          makeTextFieldIcon({
            icon: isCorrectUrl && !noWebsiteUrl ? 'check2' : undefined,
            fontSize: 16,
            color: 'var(--green-60)',
          }),
        ]}
      />

      <InputCheckbox
        checked={noWebsiteUrl}
        onChecked={handleSetNoWebsiteUrl}
        customLabel={
          <Typography
            ariaLabel='Business_info_email_label'
            variant='text-input-field-r'
            color='var(--content-primary-tertiary)'
          >
            {"I don't have a business website"}
          </Typography>
        }
        className={classNames(styles.noEmailCheckbox, { [styles.additionSpace]: noWebsiteUrl })}
      />

      {noWebsiteUrl && (
        <TextFieldWithLabel
          marginBottom={1}
          errorMessage={socialMediaUrlError}
          labelProps={{
            marginBottom: 8,
            questionTooltipProps: {
              label: "Enter the URL of your company's social media profile page",
              desc: '(e.g. Facebook, Twitter, Linkedin etc)',
              type: 'description',
              width: 226,
            },
            label: {
              text: 'Social media profile',
            },
          }}
          textFieldProps={{
            value: socialMediaUrl || '',
            error: !!socialMediaUrlError,
            onChange: setSocialMediaUrl,
            onBlur: (value: string) => checkUrl(value, 'socialMediaUrl'),
            InputProps: {
              placeholder: 'e.g. https://www.facebook.com/example/',
            },
            className: styles.selectPadding,
          }}
          rightActions={[
            makeTextFieldIcon({
              icon: isCorrectUrl && noWebsiteUrl ? 'check2' : undefined,
              fontSize: 16,
              color: 'var(--green-60)',
            }),
          ]}
        />
      )}

      <NextButton
        text='Next'
        loading={stepLoading}
        disabled={stepLoading || nextDisabled || !isCorrectUrl}
        onClick={() => moveToSecondStep(lastPathSegment as InternationalRoutes)}
      />
    </div>
  )
})
